import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { BoxT } from '@mentimeter/ragnar-ui/box';
import { Box } from '@mentimeter/ragnar-ui/box';

// https://developer.mozilla.org/en-US/docs/Web/HTTP/Browser_detection_using_the_user_agent#browser_name_and_version
const useIsSafari = () => {
  const [isSafari, setIsSafari] = useState(false);
  useEffect(() => {
    const safariUserAgent =
      navigator.userAgent.includes('Safari') &&
      !navigator.userAgent.includes('Chrome') &&
      !navigator.userAgent.includes('Chromium');
    setIsSafari(safariUserAgent);
  }, []);
  return isSafari;
};

export const ControlsWrapper = ({
  children,
  show,
  transformOrigin,
  transform,
  ...rest
}: BoxT & {
  show: boolean;
  transformOrigin?: string;
  transform?: string;
}) => {
  /*
   * note: backdropFilter and it's prefixed cousin WebkitBackdropFilter will cause the positioning in
   * DropdownMenu -> Radix -> Floating UI to break on Safari 18 and 17, respectively. Be careful if/when you're adding
   * it back.
   * Related Linear tickets:
   *   - https://linear.app/mentimeter/issue/RX-2311/dropdown-placement-in-safari
   *   - https://linear.app/mentimeter/issue/LIVE-880/popups-misplaced-on-safari-180
   */
  const isSafari = useIsSafari();
  const blurProperties = useCallback(
    (value: string) => {
      if (isSafari) {
        return {};
      }
      return {
        backdropFilter: value,
        webkitBackdropFilter: value,
      };
    },
    [isSafari],
  );
  return (
    <Box
      position="fixed"
      data-testid="controls-container"
      borderRadius="full"
      color="primary"
      {...rest}
      extend={({ theme }) => ({
        transition: `all ${theme.durations[1]}s ease`,
        opacity: show ? 1 : 0,
        pointerEvents: show ? 'auto' : 'none',
        transformOrigin,
        transform,
        ...blurProperties(theme.kosmosBlur.xl),
        backgroundColor: theme.colors.surfaceOverlayTransparent,
        ...rest?.extend?.({ theme }),
      })}
    >
      {children}
    </Box>
  );
};
